"use client";

/**
 * Counts a clip view only after the viewer has actually watched more than
 * 10 seconds of playback (wall-clock while playing, cumulative across pauses).
 * Deduped once per browser session per clip via sessionStorage.
 * Skipped when `?preview=123456789` (test bypass).
 */

import { api } from "@v1/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { type RefObject, useEffect, useRef } from "react";
import { isClipPreviewBypassActive } from "./clip-preview-bypass";

/** View qualifies only after strictly more than this many seconds watched. */
export const CLIP_VIEW_WATCH_THRESHOLD_SEC = 10;

const STORAGE_PREFIX = "clip-view-qualified-";

function alreadyQualified(clipId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(`${STORAGE_PREFIX}${clipId}`) === "1";
  } catch {
    return false;
  }
}

function markQualified(clipId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${clipId}`, "1");
  } catch {
    /* private mode / blocked storage */
  }
}

/**
 * Attach to a `<video>` ref. When cumulative play time exceeds the threshold,
 * bumps `view_count` once (same mutation for in-app detail and public share).
 */
export function useClipWatchViewTracking(
  videoRef: RefObject<HTMLVideoElement | null>,
  clipId: string | null,
  enabled: boolean,
): void {
  const incrementView = useMutation(api.clips.public.incrementClipViewById);
  const reportedRef = useRef(false);
  const watchedMsRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);

  useEffect(() => {
    reportedRef.current = alreadyQualified(clipId ?? "");
    watchedMsRef.current = 0;
    lastTickRef.current = null;
  }, [clipId]);

  useEffect(() => {
    if (!enabled || !clipId || isClipPreviewBypassActive()) return;
    const videoEl: HTMLVideoElement | null = videoRef.current;
    if (videoEl === null) return;
    const video: HTMLVideoElement = videoEl;

    if (alreadyQualified(clipId)) {
      reportedRef.current = true;
      return;
    }

    function flushTick(): void {
      if (reportedRef.current || !clipId) return;
      const now = performance.now();
      if (lastTickRef.current !== null) {
        watchedMsRef.current += now - lastTickRef.current;
      }
      lastTickRef.current = now;
      if (watchedMsRef.current <= CLIP_VIEW_WATCH_THRESHOLD_SEC * 1000) return;
      reportedRef.current = true;
      lastTickRef.current = null;
      markQualified(clipId);
      void incrementView({ clip_id: clipId });
    }

    function onPlay(): void {
      lastTickRef.current = performance.now();
    }

    function onPauseOrEnd(): void {
      flushTick();
      lastTickRef.current = null;
    }

    function onTimeUpdate(): void {
      if (video.paused || video.ended) return;
      flushTick();
    }

    video.addEventListener("play", onPlay);
    video.addEventListener("playing", onPlay);
    video.addEventListener("pause", onPauseOrEnd);
    video.addEventListener("ended", onPauseOrEnd);
    video.addEventListener("timeupdate", onTimeUpdate);

    if (!video.paused && !video.ended) {
      lastTickRef.current = performance.now();
    }

    return (): void => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("playing", onPlay);
      video.removeEventListener("pause", onPauseOrEnd);
      video.removeEventListener("ended", onPauseOrEnd);
      video.removeEventListener("timeupdate", onTimeUpdate);
      lastTickRef.current = null;
    };
  }, [videoRef, clipId, enabled, incrementView]);
}
